import { formatNOK } from "@/v2-common/utils/utils.formatting"
import Badge from "@/v2-backoffice-client/components/badge"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import {
  isLineInvoiced,
  isLineProvisional,
} from "@/v2-backoffice-client/legalBilling/legalBilling.utils"
import type { LegalBillingLine } from "@/v2-backoffice-client/legalBilling/legalBilling.types"

export type LegalBillingLineTableProps = {
  lines: LegalBillingLine[]
}

// The per-document lines of one batch: what actually goes on an invoice.
//
// Plain markup rather than the shared Table component: this is a leaf list inside a
// four-level group tree with no sorting, filtering, selection or pagination of its own —
// every one of those lives at the page level. Wiring the full table machinery here would
// add a per-batch state container for behaviour nothing uses.
function LegalBillingLineTable(props: LegalBillingLineTableProps) {
  const { lines } = props

  return (
    <table className="legal-billing-lines w-full text-sm">
      <thead>
        <tr className="text-xs text-muted-foreground text-left">
          <th className="font-normal py-1 pr-3">Document</th>
          <th className="font-normal py-1 pr-3">Type</th>
          <th className="font-normal py-1 pr-3">Purchased</th>
          <th className="font-normal py-1 pr-3 text-right">Price</th>
          <th className="font-normal py-1">Invoiced</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line) => (
          <tr
            key={line.PurchaseId}
            className="legal-billing-line border-t border-border"
            data-purchase-id={line.PurchaseId}
            data-is-invoiced={isLineInvoiced(line)}
          >
            <td className="py-1 pr-3 font-mono text-xs">{line.DocumentId}</td>
            <td className="py-1 pr-3 text-muted-foreground">
              {line.DocumentType}
            </td>
            <td className="py-1 pr-3 text-muted-foreground">
              <RelativeDate date={line.PurchasedAt} />
            </td>
            <td className="py-1 pr-3 text-right tabular-nums">
              {formatNOK(line.Price)}
              {/* The price is real expected spend but can still be withdrawn if the
                  document fails, so it must not read as a settled figure. */}
              {isLineProvisional(line) ? (
                <span className="block text-xs text-muted-foreground">
                  Processing — not settled
                </span>
              ) : null}
            </td>
            <td className="py-1">
              {isLineInvoiced(line) ? (
                <Badge variant="positive">
                  {line.InvoicedByName || `User ${line.InvoicedByUserId}`} ·{" "}
                  <RelativeDate date={line.InvoicedAt} />
                </Badge>
              ) : (
                <span className="text-xs text-muted-foreground">—</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export default LegalBillingLineTable
